import React from "react";

import {
  SkeletonAvatar,
  SkeletonBadge,
  SkeletonBlock,
  SkeletonText,
} from "../primitives";

/**
 * Configurable table row skeleton. Renders a single row matching
 * react-data-table-component row structure.
 *
 * @param {Array} columns — array of column config objects:
 *   { type: 'text'|'avatar-text'|'badge'|'date-range'|'block', width: string }
 *   Defaults to a generic 5-column layout if not provided.
 * @param {string} className
 *
 * Column type renderers:
 *   - 'text'        → SkeletonText (1-2 lines)
 *   - 'avatar-text' → SkeletonAvatar + SkeletonText side by side
 *   - 'badge'       → SkeletonBadge
 *   - 'date-range'  → two short text lines (start – end)
 *   - 'block'       → raw SkeletonBlock
 *   - 'text-badge'  → SkeletonText + SkeletonBadge stacked
 */

const DEFAULT_COLUMNS = [
  { type: "text", width: "13%" },
  { type: "text", width: "13%" },
  { type: "text", width: "15%" },
  { type: "text", width: "13%" },
  { type: "date-range", width: "12%" },
  { type: "text", width: "8%" },
  { type: "text", width: "8%" },
  { type: "text", width: "6%" },
  { type: "text", width: "11%" },
];

const renderColumn = (col, idx) => {
  const containerStyle = {
    width: col.width || "auto",
    flexShrink: 0,
    flexGrow: col.grow !== undefined ? col.grow : col.width ? 0 : 1,
    minWidth: col.minWidth || "100px",
    padding: "0 8px",
  };

  switch (col.type) {
    case "avatar-text":
      return (
        <div
          key={idx}
          style={{
            ...containerStyle,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <SkeletonAvatar size={col.avatarSize || 32} />
          <SkeletonText lines={2} widths={["80%", "55%"]} />
        </div>
      );

    case "badge":
      return (
        <div key={idx} style={containerStyle}>
          <SkeletonBadge width={col.badgeWidth || 60} />
        </div>
      );

    case "date-range":
      return (
        <div
          key={idx}
          style={{
            ...containerStyle,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 6,
          }}
        >
          <SkeletonBlock width={75} height={14} radius="sm" />
          <SkeletonBlock
            width={10}
            height={14}
            radius="sm"
            style={{ alignSelf: "center" }}
          />
          <SkeletonBlock width={75} height={14} radius="sm" />
        </div>
      );

    case "text-badge":
      return (
        <div
          key={idx}
          style={{
            ...containerStyle,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <SkeletonText lines={1} widths={["70%"]} />
          <SkeletonBadge width={col.badgeWidth || 55} />
        </div>
      );

    case "block":
      return (
        <div key={idx} style={containerStyle}>
          <SkeletonBlock
            width={col.blockWidth || "80%"}
            height={col.blockHeight || 16}
            radius="sm"
          />
        </div>
      );

    case "text":
    default:
      return (
        <div key={idx} style={containerStyle}>
          <SkeletonText
            lines={col.lines || 1}
            widths={col.widths || ["80%"]}
            size={col.size || "md"}
          />
        </div>
      );
  }
};

const TableRowSkeleton = ({ columns = DEFAULT_COLUMNS, className = "" }) => {
  return (
    <div className={`skeleton-table-row ${className}`}>
      {columns.map(renderColumn)}
    </div>
  );
};

export default TableRowSkeleton;
